import { Component, computed, effect, ElementRef, inject, input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { faRefresh, faSliders } from '@fortawesome/pro-regular-svg-icons';

import { Subscription } from 'rxjs';

import { BlockLoopSourceEnum, BlockType } from '@empathy/survey/base';

import { Question } from '@empathy/survey-core';

import { IBlockForm } from '../../../../models/blocks/block-form.interface';
import { BlockLoopSourceOptions } from '../../../../models/blocks/block-loop-source-options.constants';

import { QuestionTypeConfig } from '../../../../models/question-type-config.constants';
import { SurveyEditorService } from '../../../../services/survey-editor.service';

@Component({
  selector: 'empathy-loop-block-form',
  templateUrl: 'loop-block-form.component.html',
  styleUrls: ['./loop-block-form.component.scss']
})
export class LoopBlockFormComponent implements OnInit, OnDestroy {
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  activeBlock = input.required<FormGroup<IBlockForm>>();
  isSecondChild = input<boolean>(false);

  readonly sourceType$ = signal<BlockLoopSourceEnum>(BlockLoopSourceEnum.Question);
  readonly quantitySummary$ = signal<string>('All');
  readonly targetSummary$ = signal<string>('Not set');
  readonly participantQuantitySummary$ = signal('all 0 stimuli');
  readonly presentationOrder$ = signal('catalogue order');
  readonly stimulusCount$ = signal(0);

  filteredQuestions$ = signal<Question[]>([]);
  siblingWithoutQuestions$ = signal<boolean>(false);

  surveyEditorService = inject(SurveyEditorService);

  sourceQuestion$ = computed(() => {
    const sourceQId = this.surveyEditorService
      .surveyEditor$()
      .blocks.all$()
      .find((block) => block.id === this.activeBlock().value.id)?.sourceQId;

    return this.questions$().find((question) => question.id === sourceQId);
  });

  blockLoopSourceEnum = BlockLoopSourceEnum;
  blockLoopSourceOptions = BlockLoopSourceOptions;
  faRefresh = faRefresh;
  faSliders = faSliders;
  questionTypeConfig = QuestionTypeConfig;

  private allowedSourceQuestions: Question[];
  private questions$ = toSignal(this.surveyEditorService.questions$, { initialValue: [] as Question[] });
  private subscription: Subscription;

  constructor() {
    effect(
      (onCleanup) => {
        const form = this.activeBlock();
        this.updateRenderState();
        const subscription = form.valueChanges.subscribe(() => this.updateRenderState());
        onCleanup(() => subscription.unsubscribe());
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.updateRenderState();
    this.refreshAllowedSourceQuestions();

    this.subscription = this.surveyEditorService.surveyEditor$().blocks.refreshed$.subscribe(() => {
      if (
        this.isSecondChild() ||
        this.surveyEditorService.surveyEditor$().blocks.byGroupedBlocksId(this.activeBlock().value.id)?.type === BlockType.LoopParent
      ) {
        this.refreshAllowedSourceQuestions();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  filter(): void {
    const filterValue = this.input.nativeElement.value.toLowerCase();
    this.filteredQuestions$.update(() =>
      this.allowedSourceQuestions.filter((question) => question.id.toLowerCase().includes(filterValue) || question.title.toLowerCase().includes(filterValue))
    );
  }

  onSourceQuestionSelected(event: MatAutocompleteSelectedEvent): void {
    this.surveyEditorService.surveyEditor$().blocks.setSourceQuestion(this.activeBlock().value.id, event.option.value);
  }

  onResetSourceQId(): void {
    this.surveyEditorService.surveyEditor$().blocks.resetSourceQuestion(this.activeBlock().value.id);
    this.refreshAllowedSourceQuestions();
  }

  onSetSource(source: BlockLoopSourceEnum): void {
    this.surveyEditorService.closeLoopSettings();
    this.surveyEditorService.surveyEditor$().blocks.setSourceType(this.activeBlock().value.id, source);
  }

  onOpenSettings(trigger: HTMLElement): void {
    this.surveyEditorService.openLoopSettings(this.activeBlock(), trigger);
  }

  onRandomizationChanged(): void {
    this.surveyEditorService.surveyEditor$().blocks.refreshLists();
  }

  private updateRenderState(): void {
    this.sourceType$.set(this.activeBlock().controls.sourceType.value);
    const controls = this.activeBlock().controls;
    const quantity = controls.allocation.value?.quantity;
    const count = controls.sourceMedia.value?.length ?? 0;
    this.stimulusCount$.set(count);
    this.quantitySummary$.set(quantity === undefined ? `All (${count})` : String(quantity));
    this.participantQuantitySummary$.set(quantity === undefined ? `all ${count} stimuli` : `${quantity} stimuli`);
    this.presentationOrder$.set(controls.sourceOptionsRandomizedInd.value ? 'random order' : 'catalogue order');
    this.targetSummary$.set(String(this.activeBlock().controls.allocation.value?.completionTarget ?? 'Not set'));
  }

  private refreshAllowedSourceQuestions(): void {
    this.siblingWithoutQuestions$.set(false);

    const blocks = this.surveyEditorService.surveyEditor$().blocks;
    const blockId = this.activeBlock().value.id;
    if (blocks.byGroupedBlocksId(blockId)?.type === BlockType.LoopParent) {
      this.allowedSourceQuestions = blocks.allowedSourceQuestions(null, blockId);
      this.filteredQuestions$.set(this.allowedSourceQuestions.slice());
      return;
    }

    if (this.isSecondChild()) {
      const lastQId = this.surveyEditorService.surveyEditor$().blocks.getSiblingLastQuestionId(this.activeBlock().get('id').value);
      if (!lastQId) {
        this.resetInvalidSourceQuestion();
        this.siblingWithoutQuestions$.set(true);
        return;
      }

      // GUILLERMO: Here is where the code resets the question just added because is checking the source.
      const lastQIndex = this.surveyEditorService.surveyEditor$().questions.findIndex((q) => q.id === lastQId);
      this.allowedSourceQuestions = this.surveyEditorService.surveyEditor$().blocks.allowedSourceQuestions(lastQIndex, this.activeBlock().value.id);
      this.filteredQuestions$.set(this.allowedSourceQuestions.slice());

      if (this.allowedSourceQuestions.length === 0 && this.sourceType$() === BlockLoopSourceEnum.Question) {
        this.resetInvalidSourceQuestion();
      }
      return;
    }

    this.allowedSourceQuestions = this.surveyEditorService.surveyEditor$().blocks.allowedSourceQuestions();
    this.filteredQuestions$.set(this.allowedSourceQuestions.slice());
  }

  private resetInvalidSourceQuestion(): void {
    const block = this.activeBlock().value;
    if (block.sourceType === BlockLoopSourceEnum.Media) {
      return;
    }

    if (!block.sourceQId && block.groupedQuestionIds.length === 0) {
      return;
    }

    this.surveyEditorService.surveyEditor$().blocks.resetSourceQuestion(block.id);
  }
}
